import logger from "../../utils/logger";
import { supabase } from '../supabase/client';

// Prices shown to the user in UpgradeModal (PLN) — used to report an accurate
// Meta Pixel Purchase value once Stripe redirects back after payment.
const PLAN_PRICES_PLN = {
  starter: { monthly: 29, yearly: 290 },
  pro: { monthly: 49, yearly: 490 },
};

export const PENDING_PURCHASE_KEY = 'pcp_pending_purchase';

/**
 * Create Stripe Checkout session for subscription
 * @param {'starter'|'pro'} plan - Subscription plan (starter or pro)
 * @param {'monthly'|'yearly'} billingPeriod - Billing period (monthly or yearly)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function createCheckoutSession(plan = 'pro', billingPeriod = 'monthly') {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Get user profile for email and existing Stripe customer ID
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('email, stripe_customer_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch user profile');
    }

    // Select Price ID based on plan and billing period
    let priceId;

    if (plan === 'starter') {
      priceId = billingPeriod === 'yearly'
        ? import.meta.env.VITE_STRIPE_PRICE_ID_STARTER_YEARLY
        : import.meta.env.VITE_STRIPE_PRICE_ID_STARTER;
    } else if (plan === 'pro') {
      priceId = billingPeriod === 'yearly'
        ? import.meta.env.VITE_STRIPE_PRICE_ID_YEARLY
        : import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY;
    } else {
      throw new Error(`Unknown plan: ${plan}`);
    }

    if (!priceId) {
      throw new Error('Stripe price ID not configured for this plan');
    }

    // Get session for authorization
    const { data: { session } } = await supabase.auth.getSession();

    // Call Edge Function to create checkout session with explicit auth
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        priceId,
        customerId: profile.stripe_customer_id || undefined,
      },
      headers: session?.access_token ? {
        Authorization: `Bearer ${session.access_token}`,
      } : {},
    });

    if (error) {
      console.error('[Stripe] Edge Function error:', error);
      throw error;
    }

    if (!data.url) {
      throw new Error('No checkout URL returned from server');
    }

    // Stash the expected transaction value so the Meta Pixel Purchase event
    // fired after the Stripe redirect back can report a real amount.
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify({
      value: PLAN_PRICES_PLN[plan]?.[billingPeriod] ?? null,
      currency: 'PLN',
    }));

    // Redirect to Stripe Checkout
    window.location.href = data.url;

    return { success: true };

  } catch (error) {
    console.error('[Stripe] createCheckoutSession error:', error);
    return {
      success: false,
      error: error.message || 'Wystąpił błąd podczas tworzenia sesji płatności',
    };
  }
}

/**
 * Create Stripe Customer Portal session for managing subscription
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function createPortalSession() {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Get user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('stripe_customer_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch user profile');
    }

    if (!profile.stripe_customer_id) {
      throw new Error('No Stripe customer ID found. Please subscribe first.');
    }

    logger.log('[Stripe] Creating portal session for customer:', profile.stripe_customer_id);

    // Create portal session via Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('create-portal', {
      body: {
        customerId: profile.stripe_customer_id,
        userId: user.id,
      },
    });

    if (error) {
      console.error('[Stripe] Portal error:', error);
      throw error;
    }

    if (!data.url) {
      throw new Error('No portal URL returned from server');
    }

    logger.log('[Stripe] Portal session created, redirecting...');

    // Redirect to Stripe Portal
    window.location.href = data.url;

    return { success: true };

  } catch (error) {
    console.error('[Stripe] createPortalSession error:', error);
    return {
      success: false,
      error: error.message || 'Wystąpił błąd podczas otwierania portalu subskrypcji',
    };
  }
}

/**
 * Helper to check if Stripe is properly configured
 * @returns {boolean}
 */
export function isStripeConfigured() {
  return !!(
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY &&
    import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY &&
    import.meta.env.VITE_STRIPE_PRICE_ID_YEARLY
    // VITE_STRIPE_PRICE_ID_STARTER is optional
  );
}
